import type { ReactNode } from 'react';

/**
 * A labelled form field with a hint that becomes an error.
 *
 * Extracted from `ImportContactsDialog`, where it started, once a second and third form needed it — a
 * component that is 90% the same as an existing one is how a design system fragments (ui-ux.md § 4).
 *
 * The hint and the error occupy the SAME line rather than stacking. Two reasons: the field does not
 * change height when it becomes invalid, so a form does not reflow under the user's cursor as they
 * tab through it; and a hint that stays visible beside an error competes with the error for attention
 * at exactly the moment the error is the only thing that matters.
 *
 * The caller wires `aria-describedby` on the control itself, because only the caller knows which of
 * `{id}-hint` and `{id}-error` is live — this component cannot reach into its own children to set it.
 */
export function Field({
  id,
  label,
  hint,
  error,
  action,
  children,
}: {
  id: string;
  label: string;
  /**
   * Optional controls on the label's own line, right-aligned — a "show me this in the HTML" link, a
   * unit toggle, a count.
   *
   * Here rather than left to each caller because the alternative is a caller rendering its own row
   * above the field, which then duplicates the label: one in the row and one in the `<label>`. That
   * is what this slot exists to stop.
   */
  action?: ReactNode;
  /**
   * Optional: some fields are self-explanatory, and a hint restating the label is noise.
   *
   * Explicitly `| undefined` to match `error` below. Under `exactOptionalPropertyTypes` a bare
   * `hint?: string` means "absent or a string" and refuses `undefined` passed on purpose — which is
   * what a caller writes when the hint comes from data that may not have one.
   */
  hint?: string | undefined;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="mb-1 flex items-baseline gap-2">
        <label htmlFor={id} className="flex-1 text-sm font-medium text-heading">
          {label}
        </label>

        {action}
      </div>

      {children}

      {error === undefined ? (
        hint !== undefined && (
          <p id={`${id}-hint`} className="mt-1 text-xs text-muted">
            {hint}
          </p>
        )
      ) : (
        <p id={`${id}-error`} className="mt-1 text-xs text-danger-ink">
          {error}
        </p>
      )}
    </div>
  );
}

/** The shared input styling, so a text input in one form does not drift from the next. */
export const fieldInputClass =
  'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground ' +
  'placeholder:text-muted focus:outline-2 focus:outline-offset-1 focus:outline-primary ' +
  'aria-[invalid=true]:border-danger';
